import { useContext, useState, useEffect } from "react";
import { AppContext } from "../App";
import { FiUsers, FiAward, FiArrowRight, FiCheckCircle, FiInbox, FiBriefcase, FiMapPin, FiClock, FiFileText } from "react-icons/fi";
import bannerImg from "../assets/banner1.jpg";
import { callApi } from "../Helpers";

export default function CareerContent() {
  const appContext = useContext(AppContext);
  
  // State for jobs
  const [allJobs, setAllJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch jobs on component mount
  useEffect(() => {
    getAllJobs();
  }, []);

  function view(uuid) {
    appContext.navTo({
      item: 'side-view',
      subItem: 'job-options', 
      extraItem: uuid,
    })
  }

  // Trigger your applications drawer view 
  function openMyApplications() {
    appContext.navTo({
      item: 'side-view',
      subItem: 'my-applications'
    });
  }

  async function getAllJobs() {
    setIsLoading(true);
    return new Promise(async resolve => {
      await callApi('jobapplicant/get-open-jobs', {}).then(response => {
        if (response && response.status === 1) {
          setAllJobs(response.data);
          resolve(response.data);
        } else {
          setAllJobs([]);
          resolve(null);
        }
        setIsLoading(false);
      }).catch((err) => {
        setAllJobs([]);
        setIsLoading(false);
        resolve(null);
      });
    });
  }

  const benefits = [
    { 
      icon: <FiUsers />, 
      title: "Collaborative Culture", 
      desc: "Work alongside top-tier medical professionals in a multidisciplinary environment." 
    },
    { 
      icon: <FiAward />, 
      title: "Clinical Excellence", 
      desc: "Access to modern diagnostic tools and continuous professional development." 
    },
    { 
      icon: <FiCheckCircle />, 
      title: "Impactful Work", 
      desc: "Join a network dedicated to transforming healthcare standards in Dodoma." 
    },
  ];

  return (
    <>
     
      {/* Hero Header */}
      <section className="career-hero">
        <img src={bannerImg} className="career-bg" alt="Clinical Professionals" />
        <div className="career-overlay" />
        <div className="career-hero-content">
           <div className="career-glass-panel">
            <span className="career-label">Join Our Medical Network</span>
            <h1 className="career-title">Advancing Healthcare <br/>Through Talent.</h1>
            <p className="text-white opacity-75 mb-0" style={{ maxWidth: '500px', lineHeight: '1.7' }}>
              We are seeking dedicated professionals to join the Nanguba Healthcare Network. 
              Discover a career where clinical rigor meets compassionate service.
            </p>
          </div>
        </div>
      </section>

      {/* Value Tiers */}
      <section className="career-section">
        <div className="benefit-grid pt-5">
          {benefits.map((b, i) => (
            <div key={i} className="benefit-card">
              <div className="benefit-icon" style={{ fontSize: '2rem', color: 'var(--clinical-teal)', marginBottom: '20px' }}>
                {b.icon}
              </div>
              <h5 className="fw-bold" style={{ color: 'var(--navy-900)' }}>{b.title}</h5>
              <p className="text-muted small mb-0">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Dynamic Jobs / Empty State Container */}
        <div className="jobs-container">
          
          {/* Section Header Row */}
          <div className="section-header-row">
            <div className="text-center">
              <h2 style={{ color: 'var(--navy-900)', fontWeight: 800, marginBottom: '15px' }}>Current Openings</h2>
              <div className="m-auto m-md-0" style={{ width: '40px', height: '4px', background: 'var(--clinical-teal)', }}></div>
            </div>
            
            {/* Elegant contextual history button */}
            <button className="track-history-btn" onClick={openMyApplications}>
              <FiFileText size={16} /> My Applications
            </button>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--slate-500)' }}>
              Loading open positions...
            </div>
          ) : allJobs && allJobs.length > 0 ? (
            
            // Map through the retrieved jobs
            allJobs.map((job) => (
              <div key={job.id} className="job-card">
                <div>
                  <h4 style={{ color: 'var(--navy-900)', fontWeight: 800, margin: '0 0 8px 0' }}>
                    {job.title}
                  </h4>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {job.department && (
                      <span className="job-meta-chip"><FiBriefcase /> {job.department}</span>
                    )}
                    {job.location && (
                      <span className="job-meta-chip"><FiMapPin /> {job.location}</span>
                    )}
                    <span className="job-meta-chip"><FiClock /> {job.status || "Full-Time"}</span>
                  </div>
                </div>
                
                <button 
                  className="apply-btn btn-sm" 
                  onClick={() => view(job.uuid)}
                >
                  View Details <FiArrowRight />
                </button>
              </div>
            ))

          ) : (
            
            // Existing Empty State Component
            <div className="empty-state-card">
              <div className="empty-icon-wrapper">
                <FiInbox size={32} />
              </div>
              
              <h2 className="fw-extrabold mb-3" style={{ color: 'var(--navy-900)', fontSize: '2rem', letterSpacing: '-0.02em' }}>
                No Active Openings
              </h2>
              
              <p className="text-muted mx-auto mb-4" style={{ maxWidth: '500px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                We don't have open matching vacancies at the moment. You can still look over your historical positions or join our talent registry pool.
              </p>

              {/* Added a secondary link row directly inside empty state */}
              <div className="mb-5">
                <button 
                  onClick={openMyApplications}
                  className="btn btn-link text-decoration-none fw-bold small" 
                  style={{ color: 'var(--clinical-teal)' }}
                >
                  Already applied? View submission statuses <FiArrowRight size={14} />
                </button>
              </div>

              <div className="pt-4 border-top">
                <h6 className="text-uppercase fw-bold mb-3" style={{ color: 'var(--clinical-teal)', letterSpacing: '1px', fontSize: '0.8rem' }}>
                  Register for Future Openings
                </h6>
                <p className="mb-4 small text-muted">Send your CV and a brief introduction to our HR department.</p>
                <a 
                  href="mailto:careers@nangubahealth.com" 
                  className="d-inline-flex align-items-center gap-2 text-decoration-none fw-bold p-3 px-4 rounded-pill" 
                  style={{ background: 'var(--navy-900)', color: 'white' }}
                >
                  careers@nangubahealth.com <FiArrowRight />
                </a>
              </div>
            </div>
            
          )}
        </div>
      </section>
    </>
  );
}